import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import Select from 'react-select';
import cogoToast from 'cogo-toast';
import { confirmAlert } from 'react-confirm-alert';
import './BrandBilling.scss';

import BrandBillingWorklist from './BrandBillingWorklist';
import BrandBillingDetail from './BrandBillingDetail';
import { getBrandBasicObjects, getBrandBillingOverview, getBillingWorklist, updateBrandBilling } from '../../api/brands';

const BrandBilling = () => {
  const [worklist, setWorklist] = useState(null);
  const [brandOptions, setBrandOptions] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [overview, setOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    getBrandBasicObjects().then(brands => setBrandOptions(brands.map(brand => ({ value: brand.id, label: `${brand.name} (${brand.id})` }))));
    getBillingWorklist()
      .then(setWorklist)
      .catch(() => setWorklist(false));
  }, []);

  const openBrand = async brandOption => {
    setSelectedBrand(brandOption);
    setOverview(null);
    if (!brandOption) return;

    setIsLoading(true);
    try {
      setOverview(await getBrandBillingOverview(brandOption.value));
    } catch (e) {
      cogoToast.error(e?.response?.data?.error || 'Could not load that brand.');
      setSelectedBrand(null);
    }
    setIsLoading(false);
  };

  const reloadOverview = async () => {
    const brandId = overview.brand.id;
    try {
      const next = await getBrandBillingOverview(brandId);
      setOverview(current => (current?.brand.id === brandId ? next : current));
    } catch {
      setOverview(current => (current?.brand.id === brandId ? null : current));
      setSelectedBrand(current => (current?.value === brandId ? null : current));
      cogoToast.error('Saved, but could not refresh billing. Reopen the brand to see its current settings.');
    }
  };

  const saveBilling = async updates => {
    setIsSaving(true);
    try {
      await updateBrandBilling(overview.brand.id, updates);
      cogoToast.success('Saved.');
      await reloadOverview();
    } catch (e) {
      cogoToast.error(e?.response?.data?.error || 'Could not save that.');
    }
    setIsSaving(false);
  };

  // The click that once left 20-30 brands un-invoiced for a month never lands on one tap
  const changeCollection = value => {
    if (value === overview.commissionInvoiceCollection) return;
    if (value !== 'do_not_invoice') return saveBilling({ commissionInvoiceCollection: value });

    return confirmAlert({
      title: 'Stop invoicing their commissions?',
      message: `${overview.brand.name} gets no commission invoice at all until someone changes this back.`,
      buttons: [
        { label: 'Cancel', onClick: () => {}, className: 'cancel' },
        { label: 'Stop invoicing', onClick: () => saveBilling({ commissionInvoiceCollection: value }) }
      ]
    });
  };

  // Silencing also hands back every feature the past-due modal blocks, so it never lands on one tap either
  const changePastDueReminders = disable => {
    if (disable === !!overview.disableInAppPastDueInvoiceReminders) return;
    if (!disable) return saveBilling({ disableInAppPastDueInvoiceReminders: false });

    return confirmAlert({
      title: 'Silence past-due reminders?',
      message: `${overview.brand.name} stops seeing every in-app past-due invoice warning and gets back the features the past-due modal blocks, until someone changes this back. Add Funds still locks past the overdue allowances. Stripe’s invoice emails are unaffected.`,
      buttons: [
        { label: 'Cancel', onClick: () => {}, className: 'cancel' },
        { label: 'Silence', onClick: () => saveBilling({ disableInAppPastDueInvoiceReminders: true }) }
      ]
    });
  };

  return (
    <div className={cn('brand-billing-outer', { 'showing-detail': !!selectedBrand })}>
      <div className='brand-billing-top'>
        <div>
          <div className='brand-billing-title'>Brand Billing</div>
          {!selectedBrand && <div className='brand-billing-rule'>Commissions are set here · subscriptions are set in Stripe</div>}
        </div>
        <Select
          className='brand-billing-search'
          aria-label='Find a brand'
          options={brandOptions}
          value={selectedBrand}
          onChange={openBrand}
          placeholder='Find a brand…'
          isClearable
        />
      </div>

      {!selectedBrand && <BrandBillingWorklist worklist={worklist} onOpen={(Brand_id, name) => openBrand({ value: Brand_id, label: name })} />}
      {isLoading && <div className='brand-billing-quiet'>Loading…</div>}

      {!!overview && (
        <BrandBillingDetail
          overview={overview}
          isSaving={isSaving}
          onBack={() => openBrand(null)}
          onReload={reloadOverview}
          onChangeCollection={changeCollection}
          onChangeSource={autoChargeSource => saveBilling({ autoChargeSource })}
          onChangePastDueReminders={changePastDueReminders}
          onSaveField={saveBilling}
        />
      )}
    </div>
  );
};

export default BrandBilling;
